import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { mediaSrc } from '../api/media';
import useKidsStore from '../store/kidsStore';
import useAuthStore from '../store/authStore';

interface GroupSummary { id: number; name: string; ownerId: number; isOwner: boolean; memberCount: number; }
interface Member { type: 'PARENT' | 'KID'; id: number; name: string; profilePhotoUrl: string | null; }
interface GroupDetail { id: number; name: string; ownerId: number; isOwner: boolean; members: Member[]; }
interface Friend { friendshipId: number; parentId: number; name: string; profilePhotoUrl: string | null; }
interface MediaItem { id: number; type: string; url: string; filename: string; }

const Avatar: React.FC<{ url: string | null; size?: number }> = ({ url, size = 32 }) => (
  url ? <img src={mediaSrc(url)} alt="" style={{ width: size, height: size }} className="rounded-full object-cover" />
      : <span style={{ width: size, height: size }} className="rounded-full bg-indigo-100 inline-flex items-center justify-center text-sm">👤</span>
);

const ParentGroups: React.FC = () => {
  const { kids, fetchKids } = useKidsStore();
  const { userId } = useAuthStore();
  const navigate = useNavigate();

  const openGroupChat = async (groupId: number) => {
    try {
      const r = await api.post(`/api/parent/chats/group/${groupId}`);
      navigate(`/parent/social?tab=chat&cid=${r.data.conversationId}`);
    } catch { alert('Could not open group chat'); }
  };
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [pickedParents, setPickedParents] = useState<number[]>([]);
  const [pickedKids, setPickedKids] = useState<number[]>([]);

  const [openGroup, setOpenGroup] = useState<GroupDetail | null>(null);
  const [album, setAlbum] = useState<MediaItem[]>([]);

  const loadGroups = async () => {
    try { setGroups((await api.get('/api/parent/groups')).data); } catch {}
  };

  useEffect(() => {
    fetchKids();
    loadGroups();
    api.get('/api/parent/friends').then((r) => setFriends(r.data)).catch(() => {});
  }, [fetchKids]);

  const toggle = (arr: number[], set: (v: number[]) => void, id: number) =>
    set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const createGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post('/api/parent/groups', { name, parentIds: pickedParents, kidIds: pickedKids });
      setName(''); setPickedParents([]); setPickedKids([]); setShowCreate(false);
      loadGroups();
    } catch (err: any) { alert(err.response?.data?.message || 'Failed to create group'); }
  };

  const openDetail = async (id: number) => {
    try {
      const [d, ph, vd] = await Promise.all([
        api.get(`/api/parent/groups/${id}`),
        api.get(`/api/groups/${id}/media`, { params: { type: 'PHOTO' } }),
        api.get(`/api/groups/${id}/media`, { params: { type: 'VIDEO' } }),
      ]);
      setOpenGroup(d.data);
      setAlbum([...ph.data, ...vd.data]);
    } catch {}
  };

  const removeMember = async (m: Member) => {
    if (!openGroup) return;
    try {
      await api.delete(`/api/parent/groups/${openGroup.id}/members/${m.type}/${m.id}`);
      openDetail(openGroup.id); loadGroups();
    } catch (err: any) { alert(err.response?.data?.message || 'Failed'); }
  };

  const deleteOrLeave = async () => {
    if (!openGroup) return;
    try {
      if (openGroup.isOwner) {
        if (!window.confirm('Delete this group for everyone?')) return;
        await api.delete(`/api/parent/groups/${openGroup.id}`);
      } else {
        if (!window.confirm('Leave this group?')) return;
        await api.delete(`/api/parent/groups/${openGroup.id}/members/PARENT/${userId}`);
      }
      setOpenGroup(null); loadGroups();
    } catch (err: any) { alert(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div>
      <main className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-bold text-parentaccent">👪 Groups</h1>
          <button onClick={() => setShowCreate((s) => !s)}
            className="bg-parentaccent text-white text-sm px-4 py-2 rounded-lg">+ New group</button>
        </div>

        {showCreate && (
          <section className="bg-white rounded-2xl shadow-sm p-5">
            <form onSubmit={createGroup} className="space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Add friends</p>
                <div className="flex flex-wrap gap-2">
                  {friends.length === 0 && <span className="text-xs text-gray-400">No friends yet</span>}
                  {friends.map((f) => (
                    <label key={f.parentId} className="flex items-center gap-1.5 text-sm bg-gray-50 px-2.5 py-1 rounded-lg">
                      <input type="checkbox" checked={pickedParents.includes(f.parentId)} onChange={() => toggle(pickedParents, setPickedParents, f.parentId)} />
                      {f.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Add my kids</p>
                <div className="flex flex-wrap gap-2">
                  {kids.map((k) => (
                    <label key={k.id} className="flex items-center gap-1.5 text-sm bg-gray-50 px-2.5 py-1 rounded-lg">
                      <input type="checkbox" checked={pickedKids.includes(k.id)} onChange={() => toggle(pickedKids, setPickedKids, k.id)} />
                      {k.name}
                    </label>
                  ))}
                </div>
              </div>
              <button type="submit" className="bg-parentaccent text-white text-sm px-4 py-2 rounded-lg">Create</button>
            </form>
          </section>
        )}

        <section className="space-y-2">
          {groups.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-400">No groups yet.</div>
          ) : groups.map((g) => (
            <button key={g.id} onClick={() => openDetail(g.id)}
              className="w-full bg-white rounded-xl shadow-sm p-4 flex justify-between items-center text-left hover:bg-gray-50">
              <div>
                <p className="font-semibold text-gray-700">{g.name}</p>
                <p className="text-xs text-gray-400">{g.memberCount} members {g.isOwner && '· you own this'}</p>
              </div>
              <span className="text-gray-300">›</span>
            </button>
          ))}
        </section>
      </main>

      {/* Group detail */}
      {openGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOpenGroup(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-700">{openGroup.name}</h3>
              <button onClick={() => setOpenGroup(null)} className="text-gray-400 text-xl leading-none">×</button>
            </div>

            <p className="text-sm font-semibold text-gray-600 mb-1">Members</p>
            <div className="space-y-1 mb-4">
              {openGroup.members.map((m) => (
                <div key={`${m.type}-${m.id}`} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Avatar url={m.profilePhotoUrl} />
                    <span>{m.name} <span className="text-gray-400 text-xs">{m.type === 'KID' ? '· kid' : ''}</span></span>
                  </div>
                  {openGroup.isOwner && m.id !== openGroup.ownerId && (
                    <button onClick={() => removeMember(m)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
                  )}
                </div>
              ))}
            </div>

            <p className="text-sm font-semibold text-gray-600 mb-1">Shared album</p>
            {album.length === 0 ? (
              <p className="text-xs text-gray-400 mb-3">Nothing shared to this group yet. Upload a photo/video and pick this group.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {album.map((m) => m.type === 'PHOTO' ? (
                  <img key={m.id} src={mediaSrc(m.url)} alt={m.filename} className="w-full aspect-square object-cover rounded-lg" />
                ) : (
                  <video key={m.id} src={mediaSrc(m.url)} controls className="w-full aspect-square object-cover rounded-lg bg-black" />
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => openGroupChat(openGroup.id)}
                className="text-xs text-parentaccent border border-indigo-200 hover:bg-indigo-50 px-3 py-1.5 rounded-lg">💬 Group chat</button>
              <button onClick={deleteOrLeave}
                className="text-xs text-red-500 border border-red-200 px-3 py-1.5 rounded-lg">
                {openGroup.isOwner ? 'Delete group' : 'Leave group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentGroups;
